import { FeeAnalysisFilterRadioButton } from "@components/buttons/fee-analysis-filter-radio";
import { FilterValue } from "@components/buttons/filter";
import { SHButton, SHStack, SHTypography } from "@components/design-systems";
import { EyeIcon } from "@components/svgs/eye";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  AnalysisSummaryDTO,
  RenderAnalysisSummaryItemProps,
} from "@models/platform-analysis/entities/steps/summary";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { Box, useTheme } from "@mui/material";
import {
  PlatformFeeAnalysisInvestmentMenuFilterOptions,
  PlatformFeeAnalysisMissingInvestmentFilterOptions,
} from "@pages/platform-analysis/constant";
import { RootState } from "@redux/store";
import { hexToRGBA } from "@utils";
import { memo, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { FeeChartSection } from "../../../fee/analysis/components/fee-chart-section";

export const SummaryAppendixFeeEstimatesAnalysis = memo(
  ({ disabled: sectionDisabled }: RenderAnalysisSummaryItemProps) => {
    //#region Hooks & States
    const { palette } = useTheme();
    const { control, setValue } = useFormContext<AnalysisSummaryDTO>();
    const { setDisqualifiedHiddenIds, setDisqualifiedFeeFilter } =
      usePlatformAnalysis();
    const {
      disqualifiedFilterInvestmentMenuValue: filterInvestmentMenuValue,
      disqualifiedFilterMissingInvestmentValue: filterMissingInvestmentValue,
      disqualifiedHiddenIds: hiddenIds,
    } = useSelector(
      (state: RootState) => state.platformAnalysis.internal.feeAnalysis,
    );

    const hideChart = useWatch({
      control,
      name: "hideFeeAnalysisGraph",
    });

    const investmentMenuFilter = useMemo(
      () =>
        filterInvestmentMenuValue &&
        filterInvestmentMenuValue !== FeeInvestmentMenuOption.AllPlatform
          ? [{ label: "", value: filterInvestmentMenuValue }]
          : undefined,
      [filterInvestmentMenuValue],
    );
    const missingInvestmentFilter = useMemo(
      () =>
        filterMissingInvestmentValue &&
        filterMissingInvestmentValue !== FeeMissingInvestmentsOption.AllPlatform
          ? [{ label: "", value: filterMissingInvestmentValue }]
          : undefined,
      [filterMissingInvestmentValue],
    );

    //#region Methods
    const handleOnChangeFilter = (
      missingInvestmentFilters: FilterValue[] | undefined,
      investmentMenuFilters: FilterValue[] | undefined,
    ) => {
      setDisqualifiedHiddenIds([]);
      if (!missingInvestmentFilters && !investmentMenuFilters) {
        setDisqualifiedFeeFilter(undefined, undefined);
        setValue(
          "appendixInvestmentMenu",
          FeeInvestmentMenuOption.AllPlatform,
          {
            shouldDirty: true,
          },
        );

        setValue(
          "appendixFeeMissingInvestmentsStyle",
          FeeMissingInvestmentsOption.AllPlatform,
          {
            shouldDirty: true,
          },
        );

        return;
      }

      const investmentMenu =
        investmentMenuFilters?.[0].value ?? FeeInvestmentMenuOption.AllPlatform;
      const missingInvestment =
        missingInvestmentFilters?.[0].value ??
        FeeMissingInvestmentsOption.AllPlatform;

      setDisqualifiedFeeFilter(investmentMenu, missingInvestment);
      setValue("appendixInvestmentMenu", investmentMenu, {
        shouldDirty: true,
      });
      setValue("appendixFeeMissingInvestmentsStyle", missingInvestment, {
        shouldDirty: true,
      });
    };

    //#region Render
    return (
      <SHStack width={"100%"} gap={2}>
        <SHStack flexDirection={"row"} justifyContent={"space-between"}>
          <SHStack flexDirection={"row"} gap={"5px"}>
            <SHTypography variant="subtitle2">
              Fee estimates{" "}
              <Box component="span" fontWeight={400}>
                of platforms that made it to fee review stage but were not
                selected
              </Box>
            </SHTypography>
          </SHStack>
          <SHStack flexDirection={"row"}>
            <SHButton
              color="primary"
              variant="text"
              size="extraMedium"
              startIcon={
                <EyeIcon
                  isActive={true}
                  fill={
                    hiddenIds.length > 0
                      ? palette.primary.main
                      : palette.grey[500]
                  }
                />
              }
              disabled={hiddenIds.length === 0 || sectionDisabled}
              onClick={() => {
                setDisqualifiedHiddenIds([]);
              }}
            >
              Show hidden
            </SHButton>

            <FeeAnalysisFilterRadioButton
              labelAll={"All platforms"}
              buttonTitle={"Filters"}
              disabled={sectionDisabled}
              filterInvestmentMenuData={
                PlatformFeeAnalysisInvestmentMenuFilterOptions
              }
              filterInvestmentMenuValue={investmentMenuFilter}
              filterMissingInvestmentData={
                PlatformFeeAnalysisMissingInvestmentFilterOptions
              }
              filterMissingInvestmentValue={missingInvestmentFilter}
              width={"275px"}
              zIndex={10}
              onChangeFilter={handleOnChangeFilter}
            />
          </SHStack>
        </SHStack>
        <SHStack>
          <SHStack gap={2}>
            <FeeChartSection
              disabled={sectionDisabled}
              isAppendix={true}
              hideChart={hideChart}
              onChangeHiddenIds={(setOfIds: string[]) => {
                if (sectionDisabled) return;
                setDisqualifiedHiddenIds(setOfIds);
              }}
              getTotalPortfolioValue={(state: RootState) =>
                state.platformAnalysis?.platformAnalysis?.summary
                  ?.feePortfolioDetails?.totalPortfolioValue
              }
              getAnalysisStepFeeData={(state: RootState) =>
                state.platformAnalysis?.platformAnalysis?.summary
              }
              getContainerData={(state: RootState) =>
                state.platformAnalysis?.platformAnalysis?.summary?.feeAnalysis
                  ?.disqualified
              }
              getSubProductsFiltered={(state: RootState) =>
                state.platformAnalysis.internal.feeAnalysis
                  .disqualifiedSubProductFiltered
              }
              getHiddenIdsIndexMap={(state: RootState) =>
                state.platformAnalysis?.internal?.feeAnalysis
                  ?.disqualifiedHiddenIdsIndexMap
              }
              getHiddenIds={(state: RootState) =>
                state.platformAnalysis?.internal?.feeAnalysis
                  ?.disqualifiedHiddenIds
              }
              heatmapProps={{
                sx: {
                  p: "15px 0 0 0",
                  backgroundColor: "transparent",
                  "& .FeeChartSection-heatmap-title": {
                    border: `solid 1px ${hexToRGBA(palette.primary[50], 0.3)}`,
                    p: "10px 15px",
                  },
                },
              }}
              feeLineChartProps={{
                containerSx: {
                  backgroundColor: "transparent",
                  border: "none",
                  paddingX: 0,
                  "& .FeeLineChart-title": {
                    border: `solid 1px ${hexToRGBA(palette.primary[50], 0.3)}`,
                    p: "10px 15px",
                  },
                },
              }}
            />
          </SHStack>
        </SHStack>
      </SHStack>
    );
  },
);
